import { Box, Flex, Text, Heading, Badge, Input } from "@chakra-ui/react";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { AppButton, AppTable, TableRow, TableCell, AppSelect, AppModal } from "../../components";
import { colors } from "../../theme";
import {
  useAddCategoryRequest,
  useCategoryRequests,
  useCreateShopCategory,
  useDeclineCategoryRequest,
  useRemoveShopCategory,
} from "../../services/useQueries";
import { showToast } from "../../redux/reducer/utilsSlice";
import { formatDate } from "../../utils/format";

const WAITING_HEADERS = ["Shop", "Suggested", "Note", "Sent", ""];
const DECIDED_HEADERS = ["Shop", "Suggested", "Outcome", "When"];

const shopLine = (row) => (
  <>
    <Text fontWeight="600">{row.shopName || "A shop"}</Text>
    {!!row.mallName && (
      <Text fontSize="xs" color={colors.textSecondary}>
        {row.mallName}
      </Text>
    )}
  </>
);

const shopsText = (n) => `${n} ${n === 1 ? "shop" : "shops"}`;

/**
 * The categories shops pick from (food and drink, shopping): add or remove
 * any of them, and deal with the new ones shops suggest. Adding a suggestion
 * puts it on the list for every app and gives it to each shop that asked; it
 * can also be matched to a category already on the list. Super admin only.
 */
const CategoriesScreen = () => {
  const dispatch = useDispatch();
  const { data, isLoading, isError } = useCategoryRequests();
  const addMutation = useAddCategoryRequest();
  const createMutation = useCreateShopCategory();
  const declineMutation = useDeclineCategoryRequest();
  const removeMutation = useRemoveShopCategory();

  // The add dialog: from a shop's suggestion (`suggestion` set) or new.
  const [dialog, setDialog] = useState(null);
  const [name, setName] = useState("");
  const [group, setGroup] = useState("");

  const waiting = data?.waiting || [];
  const decided = data?.decided || [];
  const categories = data?.categories || [];
  const groupNames = data?.groups || [];
  const typed = name.trim().toLowerCase();
  // The typed name, when it's already on the list.
  const onList = typed ? categories.find((c) => c.name.toLowerCase() === typed)?.name || null : null;
  const fromSuggestion = !!dialog?.suggestion;

  const openFromSuggestion = (row) => {
    setDialog({ suggestion: row });
    setName(row.name);
    setGroup("");
  };
  const openNew = () => {
    setDialog({});
    setName("");
    setGroup("");
  };

  const confirmDialog = () => {
    if (!dialog) return;
    if (fromSuggestion) {
      if (!group && !onList) return;
      addMutation.mutate(
        { id: dialog.suggestion.id, name: name.trim() || dialog.suggestion.name, group: onList ? undefined : group },
        {
          onSuccess: (res) => {
            const result = res?.data || {};
            dispatch(
              showToast({ message: `"${result.category}" given to ${shopsText(result.shops || 1)}.`, type: "success" })
            );
            setDialog(null);
          },
        }
      );
      return;
    }
    if (!name.trim() || !group || onList) return;
    createMutation.mutate(
      { name: name.trim(), group },
      {
        onSuccess: (res) => {
          dispatch(showToast({ message: `"${res?.data?.name || name.trim()}" added to ${group}.`, type: "success" }));
          setDialog(null);
        },
      }
    );
  };

  const decline = (row) => {
    if (!window.confirm(`Decline "${row.name}" from ${row.shopName || "this shop"}?\n\nThe shop sees it wasn't added.`)) return;
    declineMutation.mutate(row.id, {
      onSuccess: () => dispatch(showToast({ message: "Declined.", type: "success" })),
    });
  };

  const remove = (category) => {
    const warning = category.shops
      ? `\n\n${shopsText(category.shops)} ${category.shops === 1 ? "has" : "have"} it and will lose it. Buyers won't find ${
          category.shops === 1 ? "that shop" : "those shops"
        } under it any more.`
      : "\n\nNo shop has it.";
    if (!window.confirm(`Remove "${category.name}" from the list?${warning}`)) return;
    removeMutation.mutate(category.id, {
      onSuccess: (res) => {
        const n = res?.data?.shops || 0;
        dispatch(
          showToast({
            message: `"${category.name}" removed${n ? `, and taken off ${shopsText(n)}` : ""}.`,
            type: "success",
          })
        );
      },
    });
  };

  const renderWaiting = (row) => (
    <TableRow key={row.id}>
      <TableCell>{shopLine(row)}</TableCell>
      <TableCell fontWeight="600">{row.name}</TableCell>
      <TableCell>
        <Text fontSize="sm" color={colors.textSecondary}>
          {row.note || "—"}
        </Text>
      </TableCell>
      <TableCell whiteSpace="nowrap">{formatDate(row.createdAt)}</TableCell>
      <TableCell>
        <Flex gap={2} justify="flex-end">
          <AppButton size="sm" variant="primary" onClick={() => openFromSuggestion(row)}>
            Add
          </AppButton>
          <AppButton size="sm" variant="secondary" onClick={() => decline(row)} disabled={declineMutation.isPending}>
            Decline
          </AppButton>
        </Flex>
      </TableCell>
    </TableRow>
  );

  const renderDecided = (row) => (
    <TableRow key={row.id}>
      <TableCell>{shopLine(row)}</TableCell>
      <TableCell>{row.name}</TableCell>
      <TableCell>
        {row.status === "ADDED" ? (
          <Badge colorPalette="green" px={2} py={1} borderRadius="full" fontSize="xs">
            {row.addedAs && row.addedAs !== row.name ? `Given "${row.addedAs}"` : "Added"}
          </Badge>
        ) : (
          <Badge colorPalette="gray" px={2} py={1} borderRadius="full" fontSize="xs">
            Declined
          </Badge>
        )}
      </TableCell>
      <TableCell whiteSpace="nowrap">{formatDate(row.decidedAt)}</TableCell>
    </TableRow>
  );

  const dialogReady = fromSuggestion ? !!group || !!onList : !!name.trim() && !!group && !onList;
  const busy = addMutation.isPending || createMutation.isPending;

  return (
    <Box>
      <Flex justify="space-between" align="flex-start" mb={6} gap={4} flexWrap="wrap">
        <Box>
          <Heading size="lg" color={colors.text} mb={1}>
            Categories
          </Heading>
          <Text color={colors.textSecondary}>
            What shops pick from. Changes show in every app straight away.
          </Text>
        </Box>
        <AppButton variant="primary" onClick={openNew}>
          Add a category
        </AppButton>
      </Flex>

      <Heading size="md" color={colors.text} mb={3}>
        Suggestions from shops{waiting.length ? ` (${waiting.length})` : ""}
      </Heading>
      <AppTable
        headers={WAITING_HEADERS}
        data={waiting}
        renderRow={renderWaiting}
        isLoading={isLoading}
        emptyMessage={isError ? "Couldn't load the suggestions. Check the connection and try again." : "No suggestions waiting."}
      />

      <Box mt={8}>
        <Heading size="md" color={colors.text} mb={1}>
          The list
        </Heading>
        <Text fontSize="sm" color={colors.textSecondary} mb={4}>
          The number is how many shops have the category. Remove one with ×.
        </Text>
        {groupNames.map((g) => (
          <Box key={g} mb={5}>
            <Text fontSize="xs" fontWeight="600" textTransform="uppercase" letterSpacing="0.04em" color={colors.textSecondary} mb={2}>
              {g}
            </Text>
            <Flex wrap="wrap" gap={2}>
              {categories
                .filter((c) => c.group === g)
                .map((c) => (
                  <Flex
                    key={c.id}
                    align="center"
                    gap={2}
                    pl={3}
                    pr={1.5}
                    py={1}
                    borderRadius="full"
                    border="1px solid"
                    borderColor="#E2E8F0"
                    bg="white"
                    fontSize="sm"
                  >
                    <Text>{c.name}</Text>
                    {c.shops > 0 && (
                      <Text
                        fontSize="xs"
                        fontWeight="600"
                        color={colors.primary}
                        title={`${shopsText(c.shops)} ${c.shops === 1 ? "has" : "have"} this category`}
                      >
                        {c.shops}
                      </Text>
                    )}
                    <button
                      type="button"
                      aria-label={`Remove ${c.name}`}
                      title={`Remove ${c.name}`}
                      onClick={() => remove(c)}
                      disabled={removeMutation.isPending}
                      style={{
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        color: "#9CA3AF",
                        fontSize: 16,
                        lineHeight: 1,
                        padding: "0 4px",
                      }}
                    >
                      ×
                    </button>
                  </Flex>
                ))}
            </Flex>
          </Box>
        ))}
      </Box>

      {decided.length > 0 && (
        <Box mt={8}>
          <Heading size="md" color={colors.text} mb={3}>
            Suggestions already dealt with
          </Heading>
          <AppTable headers={DECIDED_HEADERS} data={decided} renderRow={renderDecided} />
        </Box>
      )}

      <AppModal
        isOpen={!!dialog}
        onClose={() => setDialog(null)}
        title={fromSuggestion ? `Add "${dialog.suggestion.name}"` : "Add a category"}
        size="md"
        footer={
          <Flex gap={3} justify="flex-end" w="100%">
            <AppButton variant="secondary" onClick={() => setDialog(null)}>
              Cancel
            </AppButton>
            <AppButton variant="primary" onClick={confirmDialog} disabled={!dialogReady || busy}>
              {fromSuggestion && onList ? `Give the shop "${onList}"` : "Add"}
            </AppButton>
          </Flex>
        }
      >
        {dialog && (
          <Flex direction="column" gap={4}>
            {fromSuggestion && (
              <Text fontSize="sm" color={colors.textSecondary}>
                From {dialog.suggestion.shopName || "a shop"}
                {dialog.suggestion.note ? `: "${dialog.suggestion.note}"` : ""}
              </Text>
            )}
            <Box>
              <Text fontSize="sm" fontWeight="500" mb={1.5}>
                Name on the list
              </Text>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={40}
                placeholder="e.g. Shawarma"
                autoFocus
              />
              {fromSuggestion ? (
                <Text fontSize="xs" color={colors.textSecondary} mt={1}>
                  If it's already on the list under another name, type that name: the shop gets that category and
                  nothing new is added.
                </Text>
              ) : (
                onList && (
                  <Text fontSize="xs" color={colors.primary} mt={1}>
                    "{onList}" is already on the list.
                  </Text>
                )
              )}
            </Box>
            {fromSuggestion && onList ? (
              <Text fontSize="sm" color={colors.text}>
                "{onList}" is already on the list: the shop gets it, and nothing new is added.
              </Text>
            ) : (
              <AppSelect
                label="Group"
                placeholder="Pick a group"
                options={groupNames.map((g) => ({ value: g, label: g }))}
                value={group}
                onChange={(v) => setGroup(v || "")}
              />
            )}
          </Flex>
        )}
      </AppModal>
    </Box>
  );
};

export default CategoriesScreen;
